import React from "react";

import {
  AccountCircleRounded,
  ArrowForwardRounded,
  CategoryRounded,
  CheckBoxOutlineBlankRounded,
  CheckBoxRounded,
  DataObjectRounded,
  DnsRounded,
  IndeterminateCheckBoxRounded,
  KeyboardArrowDownRounded,
  MenuRounded,
  RefreshRounded,
} from "@mui/icons-material";
import { Box, ButtonBase, CircularProgress, Divider, IconButton, Skeleton } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import useMainState from "../../../config/context/mainStateContext";
import useModal from "../../../config/context/modalContext";
import { RequestLog } from "../../../config/services/RequestLog";
import { GreyPallet } from "../../../config/styles";
import { Dev } from "../../../config/util/consoleUtil";

import ChipMain from "../../../components/InputMain/ChipMain";
import MainModalSimple from "../../../components/MainModalSimple";
import MainSolidButton from "../../../components/MainSolidButton";
import MainText from "../../../components/MainText";
import PageHeader from "../../../components/PageHeader";

const listOfFunctions = [
  "registerToNotificationTopic",
  "getMyTopics",
  "sendNotificationToTopic",
  "sendNotificationToUser",
  "changeUserClaims",
  "setMeAsAdmin",
  "updateFoodMenu",
  "createPost",
  "saveFileToBucket",
  "getFoodMenu",
  "getAlerts",
  "triggerCreateUser",
];

export default function AdminRequestPage() {
  const navigate = useNavigate();

  const logList = RequestLog.LazyList();

  const { setShowSideMenuDrawer } = useMainState();

  const { pushAlert } = useModal();

  const [firstLoading, setFirstLoading] = React.useState(false);
  const [openModalHistory, setOpenModalHistory] = React.useState(false);

  const [selectedResources, setSelectedResources] = React.useState([...listOfFunctions]);

  const handleFetch = async () => {
    try {
      setFirstLoading(true);
      await logList.runRequest(selectedResources);
    } catch (error: any) {
      console.log(...new Dev("handleFetch", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao obter histórico",
      });
    } finally {
      setFirstLoading(false);
    }
  };

  const DisplayObjectContent = ({ itemObject }: { itemObject: any }) => {
    return (
      <>
        {Object.keys(itemObject).map((dataKey: string | number) => (
          <Box mb={1} mt={1} key={dataKey}>
            <MainText color="black" size="caption">
              <span style={{ color: GreyPallet.Regular }}>{dataKey}:</span>{" "}
              {typeof itemObject[dataKey] === "object" ? (
                <Box ml={2}>
                  <DisplayObjectContent itemObject={itemObject[dataKey]} />
                </Box>
              ) : (
                itemObject[dataKey]
              )}
            </MainText>
          </Box>
        ))}
      </>
    );
  };

  React.useEffect(() => {
    handleFetch();
  }, []);

  return (
    <Box
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
      pt={"30px"}
      pb={"45px"}
    >
      <Box width={"90%"}>
        <PageHeader
          title="Requisições"
          leftIcon={<MenuRounded />}
          onLeftAction={() => setShowSideMenuDrawer(true)}
          rightIcon={<RefreshRounded />}
          onRightAction={() => logList.runRequest(selectedResources, true)}
        />

        {/* MODAL History TODO: Apagar */}

        {openModalHistory && (
          <MainModalSimple title="Histórico" isClosable onClose={() => setOpenModalHistory(false)}>
            <Box>
              {logList.isLoading ? (
                <Box sx={{ width: "100%", textAlign: "center", py: 3 }}>
                  <CircularProgress style={{ color: GreyPallet.Regular, width: 26, height: 26 }} />
                </Box>
              ) : (
                logList.items.map((item) => (
                  <ButtonBase
                    key={item.id}
                    sx={{
                      borderRadius: "15px",
                      overflow: "hidden",
                      marginBottom: "20px",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        border: `3px solid ${GreyPallet.Light}`,
                        borderRadius: "15px",
                        width: "100%",
                        overflow: "hidden",
                        "& *": { boxSizing: "border-box" },
                      }}
                    >
                      <Box
                        style={{
                          textAlign: "start",
                          width: "100%",
                        }}
                        py={1}
                      >
                        <Box
                          px={2}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <MainText color="regular" size="small">
                            {DateTime.fromMillis(item.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
                          </MainText>
                          <MainText color="regular" size="small">
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <CategoryRounded sx={{ mr: 1, fontSize: "inherit" }} />
                              {item.resource}
                            </Box>
                          </MainText>
                        </Box>

                        <Divider
                          sx={{
                            my: 1,
                            border: `1px solid ${GreyPallet.Light}`,
                            borderRadius: "1px",
                          }}
                        />

                        <Box
                          px={2}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <MainText color="black" size="body">
                            {item.resource}
                          </MainText>

                          <MainText color="regular" size="caption">
                            {item.resource}
                          </MainText>
                        </Box>
                      </Box>
                    </Box>
                  </ButtonBase>
                ))
              )}

              {logList.hasMore && !logList.isLoading && (
                <MainSolidButton text="Carregar Mais" icon={<KeyboardArrowDownRounded />} />
              )}
            </Box>
          </MainModalSimple>
        )}

        {/* Channels */}

        <Box mt={4}>
          <Box
            mb={2}
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <MainText size="subtitle" color="black">
              Histórico
            </MainText>

            <IconButton
              color="inherit"
              onClick={() => {
                if (selectedResources.length === listOfFunctions.length) setSelectedResources([]);
                else setSelectedResources(listOfFunctions);
              }}
            >
              {selectedResources.length === listOfFunctions.length ? (
                <CheckBoxRounded />
              ) : selectedResources.length === 0 ? (
                <CheckBoxOutlineBlankRounded />
              ) : (
                <IndeterminateCheckBoxRounded />
              )}
            </IconButton>
          </Box>

          <Box mb={4} gap={1} display={"flex"} flexWrap={"wrap"}>
            <ChipMain
              options={listOfFunctions.map((item) => ({ name: item, value: item }))}
              onChange={(value: any) => {
                if (selectedResources.includes(value)) {
                  setSelectedResources(selectedResources.filter((item) => item !== value));
                } else {
                  setSelectedResources([...selectedResources, value]);
                }
              }}
              value={selectedResources}
              multiple
            />
          </Box>

          {/* List */}

          <Box>
            {firstLoading &&
              [90, 120, 100, 130].map((item) => (
                <Skeleton
                  key={item}
                  animation="wave"
                  variant="rounded"
                  width={"100%"}
                  height={item}
                  sx={{ borderRadius: "15px", mb: 2 }}
                />
              ))}

            {logList.items.map((item) => (
              <Box
                key={item.id}
                sx={{
                  border: `3px solid ${GreyPallet.Light}`,
                  borderRadius: "15px",
                  width: "100%",
                  overflow: "hidden",
                  marginBottom: "20px",
                  "& *": { boxSizing: "border-box", lineBreak: "anywhere" },
                }}
              >
                <Box
                  style={{
                    textAlign: "start",
                    width: "100%",
                  }}
                  py={1}
                >
                  <Box
                    px={2}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <MainText color="regular" size="small">
                      {DateTime.fromMillis(item.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
                    </MainText>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <CategoryRounded sx={{ mr: 1, fontSize: "inherit" }} />
                      <MainText color="black" size="caption">
                        {item.resource}
                      </MainText>
                    </Box>
                  </Box>

                  <Divider
                    sx={{
                      my: 1,
                      border: `1px solid ${GreyPallet.Light}`,
                      borderRadius: "1px",
                    }}
                  />

                  <Box
                    px={2}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {!!item.user && (
                      <Box
                        sx={{
                          justifyContent: "space-between",
                          display: "flex",
                          alignItems: "center",
                          borderRadius: "5px",
                          width: "100%",
                          mb: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <AccountCircleRounded sx={{ mr: 1 }} />
                          <MainText color="black" size="caption">
                            {item?.user?.email || "Auth Token"}
                          </MainText>
                        </Box>

                        <IconButton
                          sx={{ alignSelf: "end" }}
                          color="secondary"
                          onClick={() => navigate("/user/" + (item?.user as any)?.uid)}
                        >
                          <ArrowForwardRounded />
                        </IconButton>
                      </Box>
                    )}

                    <Box mb={2}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "start" }}>
                        <DataObjectRounded sx={{ mr: 1 }} fontSize="small" />
                        <MainText color="black" size="small">
                          Dados:
                        </MainText>
                      </Box>

                      {typeof item.data === "object" ? (
                        <DisplayObjectContent itemObject={item.data} />
                      ) : (
                        JSON.stringify(item.data)
                      )}
                    </Box>

                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "start" }}>
                        <DnsRounded sx={{ mr: 1 }} fontSize="small" />
                        <MainText color="black" size="small">
                          Requisição:
                        </MainText>
                      </Box>

                      <Box mb={1} mt={1}>
                        <MainText color="black" size="caption">
                          <span style={{ color: GreyPallet.Regular }}>Agent:</span>{" "}
                          {item.request.agent}
                        </MainText>
                      </Box>

                      <Box mb={1}>
                        <MainText color="black" size="caption">
                          <span style={{ color: GreyPallet.Regular }}>IP:</span> {item.request.ip}
                        </MainText>
                      </Box>

                      <MainText color="black" size="caption">
                        <span style={{ color: GreyPallet.Regular }}>Origem:</span>{" "}
                        {item.request.origin}
                      </MainText>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}

            {logList.hasMore && !firstLoading && (
              <MainSolidButton
                loading={logList.isLoading}
                text="Carregar Mais"
                icon={<KeyboardArrowDownRounded />}
                onClick={() => logList.runRequest(selectedResources)}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
